import { useMemo, useState } from "react";
import SEO from "../components/SEO";
import FAQSection from "../components/FAQSection";
import PageHero from "../components/PageHero";
import PageIntro from "../components/PageIntro";
import heroContact from "../assets/heroes/hero-contact.webp";
import { useApi } from "../utils/Api";
import { Loader2, CheckCircle2, MapPin, Phone, Mail } from "lucide-react";
import CtaBand from "../components/CtaBand";
import ctaPortrait from "../assets/heroes/cta-portrait.webp";
import { PAGES } from "../seoContent";
import { whatsappLink, WHATSAPP_MESSAGES } from "../utils/whatsapp";

const SOCIALS = [
  { name: "Instagram", icon: "fa-instagram", href: "https://www.instagram.com/kinewsstudioltd" },
  {
    name: "Facebook",
    icon: "fa-facebook",
    href: "https://web.facebook.com/profile.php?id=100064556025543&locale=mt_MT#",
  },
  { name: "TikTok", icon: "fa-tiktok", href: "https://www.tiktok.com/@kinewsstudio.co.uk" },
  {
    name: "YouTube",
    icon: "fa-youtube",
    href: "https://www.youtube.com/channel/UCNEY7P-m85b--rapXa8Zncg",
  },
  { name: "WhatsApp", icon: "fa-whatsapp", href: whatsappLink(WHATSAPP_MESSAGES.general) },
];

export default function Contact() {
  const { apiFetch } = useApi();
  const todayName = useMemo(
    () =>
      new Date().toLocaleDateString("en-GB", {
        weekday: "long",
        timeZone: "Europe/London",
      }),
    [],
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ success: false, message: "" });

  const onChange = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setStatus({ success: false, message: "" });

      const res = await apiFetch("submit_contact", form);
      if (res.success) {
        setStatus({ success: true, message: res.message });
        setForm({ name: "", email: "", message: "" });
      } else {
        setStatus({ success: false, message: res.message || "Failed to send message." });
      }
    } catch (err) {
      setStatus({ success: false, message: "An error occurred. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const address =
    "Unit 2, Beechwood House, Milnrow Road, Rochdale, Manchester OL16 2AA";
  const phone = "+44 7486 160965";
  const email = "info@kinewsstudiolimited.co.uk";

  const mapSrc =
    "https://www.google.com/maps?q=Unit%202,%20Beechwood%20House,%20Milnrow%20Road,%20Rochdale,%20Manchester%20OL16%202AA&output=embed";
  const mapLink =
    "https://www.google.com/maps/search/?api=1&query=Unit%202%2C%20Beechwood%20House%2C%20Milnrow%20Road%2C%20Rochdale%20OL16%202AA";

  const cardClass =
    "rounded-3xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] md:p-8";
  const inputClass =
    "w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-base text-black outline-none transition placeholder:text-black/40 focus:border-pink-500";
  const rowClass =
    "flex items-start gap-4 rounded-2xl border border-black/10 p-4 transition hover:border-pink-300 hover:bg-pink-50/50";
  const iconClass =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-pink-600";

  return (
    <>
      <SEO
        title={PAGES["/contact"].title}
        description={PAGES["/contact"].description}
      />
      <main className="min-h-screen bg-white text-black">
        <PageHero
          title="Get in touch"
          image={heroContact}
          imagePosition="center 30%"
        />

        <PageIntro>
          Have a question about a booking, an event or a custom request? Send us
          a message, give us a call or visit the studio in Rochdale. We
          typically reply within 24 hours.
        </PageIntro>

        <section className="bg-[#fff7fb]">
          <div className="mx-auto max-w-7xl px-6 pb-16 pt-8 sm:px-10 lg:px-16 xl:px-6">
            <div className="grid gap-8 lg:grid-cols-12">
              {/* LEFT */}
              <div className="min-w-0 space-y-8 lg:col-span-5">
                <div className={cardClass}>
                  <h2 className="text-2xl font-semibold">Contact details</h2>

                  <div className="mt-6 space-y-3">
                    <a href={mapLink} target="_blank" rel="noopener noreferrer" className={rowClass}>
                      <span className={iconClass}>
                        <MapPin size={20} />
                      </span>
                      <span>
                        <span className="block text-sm text-black/50">Visit us</span>
                        <span className="block font-medium text-black">{address}</span>
                      </span>
                    </a>

                    <a href="tel:+447486160965" className={rowClass}>
                      <span className={iconClass}>
                        <Phone size={20} />
                      </span>
                      <span>
                        <span className="block text-sm text-black/50">Call us</span>
                        <span className="block font-medium text-black">{phone}</span>
                      </span>
                    </a>

                    <a href="tel:01706803538" className={rowClass}>
                      <span className={iconClass}>
                        <Phone size={20} />
                      </span>
                      <span>
                        <span className="block text-sm text-black/50">Landline</span>
                        <span className="block font-medium text-black">01706 803 538</span>
                      </span>
                    </a>

                    <a href={`mailto:${email}`} className={rowClass}>
                      <span className={iconClass}>
                        <Mail size={20} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-black/50">Email us</span>
                        <span className="block break-words font-medium text-black">{email}</span>
                      </span>
                    </a>
                  </div>

                  <div className="mt-6">
                    <p className="text-sm text-black/50">Follow us on social media</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {SOCIALS.map((s) => (
                        <a
                          key={s.name}
                          href={s.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={s.name}
                          title={s.name}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-lg text-pink-600 transition hover:-translate-y-0.5 hover:border-pink-500 hover:bg-pink-500 hover:text-white"
                        >
                          <i className={`fab ${s.icon}`} aria-hidden="true"></i>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                {/* HOURS */}
                <div className="rounded-3xl border border-pink-500/20 bg-[#1c1c1c] p-7 text-white shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
                  <h3 className="text-xl font-semibold">Opening hours</h3>

                  <ul className="mt-4 space-y-2 text-sm">
                    <li
                      className={[
                        "flex justify-between border-b border-white/10 pb-2",
                        todayName !== "Sunday" ? "font-semibold text-pink-400" : "text-white/75",
                      ].join(" ")}
                    >
                      <span>Monday - Saturday</span>
                      <span>9am - 6pm</span>
                    </li>
                    <li
                      className={[
                        "flex justify-between",
                        todayName === "Sunday" ? "font-semibold text-pink-400" : "text-white/75",
                      ].join(" ")}
                    >
                      <span>Sunday</span>
                      <span>Closed</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* RIGHT */}
              <div className="min-w-0 space-y-8 lg:col-span-7">
                <div className={cardClass}>
                  <h2 className="text-2xl font-semibold">Send a message</h2>

                  {status.success ? (
                    <div className="mt-6 rounded-2xl border border-green-100 bg-green-50 p-8 text-center">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                        <CheckCircle2 size={24} />
                      </div>
                      <h3 className="mb-2 text-lg font-bold text-green-900">Message Sent!</h3>
                      <p className="mb-6 text-sm text-green-700">{status.message}</p>
                      <button
                        type="button"
                        onClick={() => setStatus({ success: false, message: "" })}
                        className="text-sm font-bold text-green-900 underline underline-offset-4"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={onSubmit} className="mt-6 space-y-5">
                      <div className="grid gap-5 md:grid-cols-2">
                        <input
                          className={inputClass}
                          placeholder="Full name"
                          value={form.name}
                          onChange={onChange("name")}
                          required
                        />

                        <input
                          className={inputClass}
                          placeholder="Email"
                          type="email"
                          value={form.email}
                          onChange={onChange("email")}
                          required
                        />
                      </div>

                      <textarea
                        className={`${inputClass} resize-none`}
                        rows={3}
                        placeholder="How can we help?"
                        value={form.message}
                        onChange={onChange("message")}
                        required
                      />

                      {status.message && !status.success && (
                        <p className="text-sm text-red-500">{status.message}</p>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-3 rounded-2xl bg-pink-500 px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition hover:bg-pink-600 disabled:bg-pink-500/40"
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Sending...
                          </>
                        ) : (
                          "Send message"
                        )}
                      </button>

                      <p className="text-sm text-black/50">
                        We typically reply within 24 hours.
                      </p>
                    </form>
                  )}
                </div>

                {/* MAP */}
                <div className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
                  <div className="px-6 py-5 md:px-8">
                    <h2 className="text-2xl font-semibold">Find us</h2>
                  </div>

                  <div className="h-[360px] w-full">
                    <iframe
                      src={mapSrc}
                      className="h-full w-full"
                      loading="lazy"
                      title="Google Maps location of Kinews Studio"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 lg:px-16 xl:px-6">
            <CtaBand
              heading="View Our Rochdale Photography Gallery"
              text="Browse studio portraits, family sessions and maternity shoots from our Rochdale studio."
              to="/gallery"
              label="See Our Work"
              image={ctaPortrait}
            />
          </div>
        </section>
        <FAQSection
          title="Questions about booking or events?"
          items={PAGES["/"].faqs}
          markup={false}
        />
      </main>
    </>
  );
}
